import { useState, useEffect, useCallback } from 'react';
import { medicalReservationApiService } from '../services/medicalReservationApiService';
import { handleApiError } from '../utils/apiClient';
import { logger } from '../utils/debugLogger';
import type {
  MedicalReservationRecord,
  MedicalReservationListResponse,
  MedicalReservationFilterOptions,
  MedicalReservationListState,
  PasteStatusResponse,
  ActionStatus,
} from '../types/medicalReservation';

/**
 * 診療予約データ一覧管理用カスタムフック
 * 
 * 意図：
 * - 診療予約レコードの取得とステート管理の一元化
 * - フィルタリング・検索・ページング機能の実装
 * - エラーハンドリングとローディング状態の管理
 * - CRUD操作の統合管理
 * 
 * 注意点：
 * - APIエラー時のリトライ処理
 * - メモリリークの防止
 * - 不要な再レンダリングの抑制
 * 
 * 改善提案：
 * - リアルタイム更新機能（WebSocket）
 * - オプティミスティックアップデート
 * - キャッシュ機能の実装
 */
export const useMedicalReservationList = () => {
  const [state, setState] = useState<MedicalReservationListState>({
    data: [],
    meta: null,
    isLoading: false,
    error: null,
    filters: {
      page: 1,
      page_size: 100,
      sort_by: 'consultationStartTime',
      sort_order: 'desc'
    },
    selectedRecord: null,
    detailModalOpen: false,
    editModalOpen: false,
    assignModalOpen: false,
    selectedIds: []
  });

  // 貼り付け機能用の状態
  const [pasteState, setPasteState] = useState<{
    isProcessing: boolean;
    pasteDialogOpen: boolean;
    pasteJobId: string | null;
    pasteStatus: PasteStatusResponse['data'] | null;
  }>({
    isProcessing: false,
    pasteDialogOpen: false,
    pasteJobId: null,
    pasteStatus: null,
  });

  /**
   * 診療予約データ一覧を取得
   */
  const fetchData = useCallback(async (filters?: MedicalReservationFilterOptions) => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const mergedFilters = { ...state.filters, ...filters };
      const response = await medicalReservationApiService.getMedicalReservations(mergedFilters);

      setState(prev => ({
        ...prev,
        data: response.data || [],
        meta: response.meta || null,
        isLoading: false,
        filters: mergedFilters
      }));
    } catch (error) {
      const errorMessage = handleApiError(error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage
      }));
    }
  }, [state.filters]);

  /**
   * 初回データ取得
   */
  useEffect(() => {
    fetchData();
  }, []);

  /**
   * フィルター更新
   */
  const updateFilters = useCallback((newFilters: Partial<MedicalReservationFilterOptions>) => {
    const updatedFilters = { ...state.filters, ...newFilters };
    fetchData(updatedFilters);
  }, [fetchData]);

  /**
   * ページ変更
   */
  const handlePageChange = useCallback((page: number) => {
    updateFilters({ page });
  }, [updateFilters]);

  /**
   * ページサイズ変更
   */
  const handlePageSizeChange = useCallback((pageSize: number) => {
    updateFilters({ page: 1, page_size: pageSize });
  }, [updateFilters]);

  /**
   * ソート変更
   */
  const handleSort = useCallback((sortBy: keyof MedicalReservationRecord) => {
    const isAsc = state.filters.sort_by === sortBy && state.filters.sort_order === 'asc';
    updateFilters({
      sort_by: sortBy,
      sort_order: isAsc ? 'desc' : 'asc'
    });
  }, [state.filters.sort_by, state.filters.sort_order, updateFilters]);

  /**
   * 検索実行
   */
  const handleSearch = useCallback((search: string) => {
    updateFilters({ search, page: 1 });
  }, [updateFilters]);

  /**
   * データリフレッシュ
   */
  const refresh = useCallback(() => {
    fetchData(state.filters);
  }, [fetchData, state.filters]);

  /**
   * 詳細モーダル表示
   */
  const showDetails = useCallback((record: MedicalReservationRecord) => {
    setState(prev => ({
      ...prev,
      selectedRecord: record,
      detailModalOpen: true
    }));
  }, []);

  /**
   * 振り分けモーダル表示
   */
  const showAssignModal = useCallback((record: MedicalReservationRecord) => {
    setState(prev => ({
      ...prev,
      selectedRecord: record,
      assignModalOpen: true
    }));
  }, []);

  /**
   * モーダルを閉じる
   */
  const closeModals = useCallback(() => {
    setState(prev => ({
      ...prev,
      selectedRecord: null,
      detailModalOpen: false,
      assignModalOpen: false
    }));
  }, []);

  /**
   * 記録削除
   */
  const deleteRecord = useCallback(async (id: string): Promise<boolean> => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));
      
      await medicalReservationApiService.deleteMedicalReservation(id);
      
      // 削除後にデータをリフレッシュ
      await fetchData(state.filters);
      
      setState(prev => ({ ...prev, isLoading: false }));
      closeModals();
      
      return true;
    } catch (error) {
      const errorMessage = handleApiError(error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage
      }));
      return false;
    }
  }, [fetchData, state.filters, closeModals]);

  /**
   * レコード振り分け
   */
  const assignRecord = useCallback(async (id: string, assignedTo: string, notes?: string): Promise<boolean> => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));
      
      await medicalReservationApiService.updateMedicalReservation(id, {
        assignedTo,
        notes
      });
      
      // 更新後にデータをリフレッシュ
      await fetchData(state.filters);
      
      setState(prev => ({ ...prev, isLoading: false }));
      closeModals();
      
      return true;
    } catch (error) {
      const errorMessage = handleApiError(error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage
      }));
      return false;
    }
  }, [fetchData, state.filters, closeModals]);

  /**
   * エラーをクリア
   */
  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  /**
   * 貼り付けダイアログを開く
   */
  const openPasteDialog = useCallback(() => {
    setPasteState(prev => ({
      ...prev,
      pasteDialogOpen: true,
      pasteStatus: null,
    }));
  }, []);

  /**
   * 貼り付けダイアログを閉じる
   */
  const closePasteDialog = useCallback(() => {
    setPasteState(prev => ({
      ...prev,
      pasteDialogOpen: false,
      pasteJobId: null,
      pasteStatus: null,
      isProcessing: false,
    }));
  }, []);

  /**
   * 選択されたレコードを貼り付け
   */
  const pasteSelectedRecords = useCallback(async () => {
    const timer = logger.startTimer('useMedicalReservationList', 'pasteSelectedRecords');
    
    if (state.selectedIds.length === 0) {
      logger.warn('useMedicalReservationList', 'pasteSelectedRecords:中止', {
        理由: '選択されたレコードがありません'
      });
      return;
    }

    logger.info('useMedicalReservationList', 'pasteSelectedRecords:開始', {
      selectedIds: state.selectedIds,
      selectedCount: state.selectedIds.length,
      selectedRecords: state.data
        .filter(r => state.selectedIds.includes(r.id))
        .map(r => ({
          id: r.id,
          doctorInCharge: r.doctorInCharge,
          patientName: r.patientName,
          anybotId: r.anybotId
        }))
    });

    setPasteState(prev => ({ ...prev, isProcessing: true }));

    try {
      // 貼り付けAPIを呼び出し
      logger.debug('useMedicalReservationList', 'pasteSelectedRecords:API呼び出し', {
        selectedIds: state.selectedIds
      });
      
      const response = await medicalReservationApiService.pasteToSpreadsheet(state.selectedIds);
      
      logger.info('useMedicalReservationList', 'pasteSelectedRecords:API応答', {
        success: response.success,
        jobId: response.data?.job_id,
        totalCount: response.data?.total_count,
        doctorCount: response.data?.doctors?.length
      });
      
      if (response.success && response.data) {
        setPasteState(prev => ({
          ...prev,
          pasteJobId: response.data!.job_id,
        }));

        // 進捗をポーリング
        logger.debug('useMedicalReservationList', 'pasteSelectedRecords:ポーリング開始', {
          jobId: response.data.job_id
        });
        
        const finalStatus = await medicalReservationApiService.pollPasteStatus(
          response.data.job_id,
          (status) => {
            logger.debug('useMedicalReservationList', 'pasteSelectedRecords:進捗更新', {
              jobId: response.data!.job_id,
              status: status.status,
              progress: status.progress,
              completedCount: status.completed_count,
              totalCount: status.total_count
            });
            
            setPasteState(prev => ({
              ...prev,
              pasteStatus: status,
            }));
          }
        );

        logger.info('useMedicalReservationList', 'pasteSelectedRecords:処理完了', {
          status: finalStatus.status,
          totalCount: finalStatus.total_count,
          completedCount: finalStatus.completed_count,
          results: finalStatus.results?.map(r => ({
            doctorCode: r.doctor_code,
            doctorName: r.doctor_name,
            success: r.success,
            recordCount: r.record_count,
            hasUrl: r.spreadsheet_url !== undefined,
            error: r.error
          }))
        });

        // 成功時はデータをリフレッシュとステータス更新
        if (finalStatus.status === 'completed') {
          const selectedCount = state.selectedIds.length;
          
          logger.debug('useMedicalReservationList', 'pasteSelectedRecords:フラグ更新', {
            updatingRecords: state.selectedIds
          });
          
          // フロントエンド側で診療リスト貼り付け完了フラグを更新
          setState(prev => ({
            ...prev,
            data: prev.data.map(record =>
              prev.selectedIds.includes(record.id)
                ? { ...record, paste_flag: true }
                : record
            ),
            selectedIds: []
          }));
          
          // メッセージを設定
          finalStatus.message = `${selectedCount}件の貼り付けが完了し、診療リスト貼り付けを完了しました`;
        }

        // 処理完了（ステータス更新後に最終状態を設定）
        setPasteState(prev => ({
          ...prev,
          isProcessing: false,
          pasteStatus: finalStatus,
        }));
        
        timer({
          success: true,
          status: finalStatus.status,
          totalCount: finalStatus.total_count
        });
      } else {
        const errorMessage = response.message || '貼り付け処理の開始に失敗しました';
        logger.error('useMedicalReservationList', 'pasteSelectedRecords:開始失敗', 
          new Error(errorMessage), {
            response
          });
        throw new Error(errorMessage);
      }
    } catch (error) {
      const errorMessage = handleApiError(error);
      
      logger.error('useMedicalReservationList', 'pasteSelectedRecords:エラー', error, {
        selectedIds: state.selectedIds,
        errorMessage
      });
      
      setPasteState(prev => ({
        ...prev,
        isProcessing: false,
        pasteStatus: {
          job_id: '',
          status: 'failed',
          progress: 0,
          total_count: 0,
          completed_count: 0,
          error_message: errorMessage,
        },
      }));
      
      timer({
        success: false,
        error: errorMessage
      });
    }
  }, [state.selectedIds, state.filters, fetchData]);

  /**
   * レコード選択のトグル
   */
  const toggleSelectRecord = useCallback((id: string) => {
    setState(prev => ({
      ...prev,
      selectedIds: prev.selectedIds.includes(id)
        ? prev.selectedIds.filter(selectedId => selectedId !== id)
        : [...prev.selectedIds, id]
    }));
  }, []);

  /**
   * レコードが選択可能かどうか判定
   * 業務ステータスが「完了」または過去の診療時間の場合は選択不可
   */
  const isRecordSelectable = useCallback((record: MedicalReservationRecord): boolean => {
    try {
      // 診療開始時間が過去かチェック
      const consultationDate = new Date(record.consultationStartTime);
      const now = new Date();
      const isPast = consultationDate < now;

      // 業務ステータスが「完了」かチェック
      const isBusinessCompleted = record.businessStatus === 'completed';

      // 過去の診療時間 OR 業務ステータスが「完了」の場合は選択不可
      return !(isPast || isBusinessCompleted);
    } catch {
      return true; // エラー時は選択可能とする
    }
  }, []);

  /**
   * 全レコード選択（選択可能なレコードのみ）
   */
  const selectAllRecords = useCallback(() => {
    setState(prev => ({
      ...prev,
      selectedIds: prev.data
        .filter(record => isRecordSelectable(record))
        .map(record => record.id)
    }));
  }, [isRecordSelectable]);

  /**
   * 選択クリア
   */
  const clearSelection = useCallback(() => {
    setState(prev => ({
      ...prev,
      selectedIds: []
    }));
  }, []);

  /**
   * 身分証明書確認のトグル
   */
  const toggleIdentityVerified = useCallback(async (record: MedicalReservationRecord): Promise<boolean> => {
    try {
      const newValue = !record.identity_verification_flag;

      // 楽観的更新: 即座にローカル状態を更新（リロードを防ぐ）
      setState(prev => ({
        ...prev,
        data: prev.data.map(r =>
          r.id === record.id
            ? { ...r, identity_verification_flag: newValue }
            : r
        )
      }));

      // バックグラウンドでAPIを呼び出して更新
      try {
        await medicalReservationApiService.updateMedicalReservation(record.id, {
          identity_verification_flag: newValue
        });
        return true;
      } catch (apiError) {
        // API失敗時は元の値に戻す
        setState(prev => ({
          ...prev,
          data: prev.data.map(r =>
            r.id === record.id
              ? { ...r, identity_verification_flag: !newValue }  // 元の値に戻す
              : r
          ),
          error: handleApiError(apiError)
        }));
        return false;
      }
    } catch (error) {
      const errorMessage = handleApiError(error);
      setState(prev => ({
        ...prev,
        error: errorMessage
      }));
      return false;
    }
  }, []);

  /**
   * 通知ステータスのトグル
   */
  const toggleNotificationStatus = useCallback(async (record: MedicalReservationRecord): Promise<boolean> => {
    try {
      // 通知ステータスのトグル: 'none' ⇔ 'notification_required'
      const newStatus: ActionStatus = record.actionStatus === 'notification_required' ? 'none' : 'notification_required';

      // 楽観的更新: 即座にローカル状態を更新
      setState(prev => ({
        ...prev,
        data: prev.data.map(r =>
          r.id === record.id
            ? { ...r, actionStatus: newStatus }
            : r
        )
      }));

      // バックグラウンドでAPIを呼び出して更新
      try {
        await medicalReservationApiService.updateMedicalReservation(record.id, {
          actionStatus: newStatus
        });
        return true;
      } catch (apiError) {
        // API失敗時は元の値に戻す
        setState(prev => ({
          ...prev,
          data: prev.data.map(r =>
            r.id === record.id
              ? { ...r, actionStatus: record.actionStatus }  // 元の値に戻す
              : r
          ),
          error: handleApiError(apiError)
        }));
        return false;
      }
    } catch (error) {
      const errorMessage = handleApiError(error);
      setState(prev => ({
        ...prev,
        error: errorMessage
      }));
      return false;
    }
  }, []);

  return {
    // 状態
    ...state,
    ...pasteState,

    // データ操作
    fetchData,
    updateFilters,
    handlePageChange,
    handlePageSizeChange,
    handleSort,
    handleSearch,
    refresh,

    // モーダル操作
    showDetails,
    showAssignModal,
    closeModals,

    // CRUD操作
    deleteRecord,
    assignRecord,
    toggleIdentityVerified,
    toggleNotificationStatus,

    // 選択操作
    toggleSelectRecord,
    selectAllRecords,
    clearSelection,

    // 貼り付け操作
    openPasteDialog,
    closePasteDialog,
    pasteSelectedRecords,

    // ユーティリティ
    clearError
  };
}; 